import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Button,
  FormControlLabel,
  Tooltip,
  Popover,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import Label from "../../../../components/Label/Label";

const CreateChartsPanel = ({
  setCharts,
  charts,
  activeId,
  setAnchorEl,
  setActiveId,
  anchorEl,
  handleAddChart,
  setShowConfigPanel,
  setSelectedTab,
  setSaveDialogOpen,
  disableAllFields,
  showSummaryTable,
  setShowSummaryTable,
  showViolationTable,
  setShowViolationTable,
  handleRefresh,
  saveDisabled,
  setShowPreview,
  typeOfMode,
  vizName,
  canCreateChart,
  canEditChart,
  canDeleteChart,
  canSaveChart,
}) => {
  const [refreshAnchor, setRefreshAnchor] = useState(null);

  const openRefreshPopover = (e) => {
    setRefreshAnchor(e.currentTarget);
  };

  const closeRefreshPopover = () => {
    setRefreshAnchor(null);
  };

  const onRefreshClick = (type) => {
    handleRefresh(type);
    closeRefreshPopover();
  };

  // ---------- Handlers ----------

  const handleToggleSelect = (id) => {
    setCharts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c)),
    );
  };

  const handleNameChange = (id, value) => {
    setCharts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, name: value } : c)),
    );
  };

  const handleDelete = () => {
    if (!canDeleteChart) return;
    setCharts((prev) => prev.filter((c) => c.id !== activeId));
    handleMenuClose();
  };

  const handleEdit = () => {
    if (!canEditChart) return;
    setCharts((prev) =>
      prev.map((c) => (c.id === activeId ? { ...c, isEditing: true } : c)),
    );
    handleMenuClose();
    setShowConfigPanel(true);
    setSelectedTab("parameter");
  };

  const handleBlur = (id) => {
    setCharts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, isEditing: false } : c)),
    );
  };

  const handleMenuOpen = (e, id) => {
    if (!canEditChart && !canDeleteChart) return;
    setAnchorEl(e.currentTarget);
    setActiveId(id);
    setShowConfigPanel(false);
    setShowPreview(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isChartValid = (chart) => {
    const ps = chart?.parameterState;

    return (
      !!chart?.name?.trim() &&
      !!ps?.product?.id &&
      !!ps?.site?.id &&
      !!ps?.unitOfOperation?.id &&
      !!ps?.parameter?.parameterId &&
      !!ps?.plotType
    );
  };

  const isSaveDisabled =
    disableAllFields || charts.some((c) => !isChartValid(c) || c.isError);

  return (
    <Box className="side-card-wrapper">
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Label
          text={
            typeOfMode === "edit"
              ? `Saved Charts - ${vizName || ""}`
              : "Saved Charts"
          }
          fontSize="14px"
          fontWeight="500"
        />

        <Tooltip title="Refresh data" arrow>
          <IconButton
            size="small"
            onClick={openRefreshPopover}
            disabled={typeOfMode === "create"}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ pt: 0.5 }} />

      <Popover
        open={Boolean(refreshAnchor)}
        anchorEl={refreshAnchor}
        onClose={closeRefreshPopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            p: 2,
            borderRadius: 2,
            width: 220,
          },
        }}
      >
        <Typography fontSize="13px" fontWeight={500} mb={1}>
          Refresh Options
        </Typography>

        <Button
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mb: 1, textTransform: "none" }}
          onClick={() => onRefreshClick("existing")}
        >
          Refresh On Existing Batches
        </Button>

        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ textTransform: "none" }}
          onClick={() => onRefreshClick("include_new")}
        >
          Refresh On New Batches
        </Button>
      </Popover>

      <Box
        sx={{
          mt: 1,
          borderRadius: 1,
          border: "1px solid #eee",
          flex: 1,
          overflowY: "auto",
        }}
      >
        {charts.map((chart) => (
          <Box
            key={chart.id}
            display="flex"
            alignItems="center"
            px={1}
            py={0.5}
            mb={0.5}
            border="1px solid #f0f0f0"
            borderRadius={1}
            bgcolor="#fff"
          >
            <Checkbox
              checked={chart.selected}
              size="small"
              onChange={() => handleToggleSelect(chart.id)}
            />

            <TextField
              variant="standard"
              value={chart.name}
              placeholder="Chart name"
              fullWidth
              onChange={(e) => handleNameChange(chart.id, e.target.value)}
              onBlur={() => handleBlur(chart.id)}
              error={!chart.name?.trim() || chart.isError}
              helperText={
                !chart.name?.trim()
                  ? "Chart name is required"
                  : chart.isError
                    ? chart.errorMessage
                    : ""
              }
            />

            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, chart.id)}
              disabled={!canEditChart && !canDeleteChart}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box mt={2}>
        <Label text="Display Options" fontSize="14px" fontWeight="500" />

        <FormControlLabel
          control={
            <Checkbox
              checked={showSummaryTable}
              onChange={(e) => setShowSummaryTable(e.target.checked)}
              size="small"
            />
          }
          label="Show Summary Table"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={showViolationTable}
              onChange={(e) => setShowViolationTable(e.target.checked)}
              size="small"
            />
          }
          label="Show Violation Table"
        />
      </Box>

      {canSaveChart && <Tooltip
        title={isSaveDisabled ? "Complete configuration to enable saving" : ""}
        arrow
      >
        <span>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setSaveDialogOpen(true)}
            disabled={isSaveDisabled || saveDisabled}
          >
            Save
          </Button>
        </span>
      </Tooltip>}

      {canCreateChart && <Button
        variant="outlined"
        fullWidth
        sx={{ mt: 1 }}
        onClick={handleAddChart}
        startIcon={<AddIcon />}
      >
        Create New Chart
      </Button>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {canEditChart && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
        {canDeleteChart && <MenuItem onClick={handleDelete}>Delete</MenuItem>}
      </Menu>
    </Box>
  );
};

export default CreateChartsPanel;
